/**
 * Comparacao com o periodo anterior e economia unitaria, tudo em inteiros.
 *
 * Copia deliberada (e menor) da regra da Central: os modulos nascem em
 * paralelo e um import cruzado quebraria um quando o outro mudasse.
 *
 * Decisoes nao obvias:
 *  1. A variacao relativa sai em BPS inteiros (1234 = +12,34%), calculada uma
 *     vez aqui. Nenhum componente faz `(a - b) / b` por conta propria.
 *  2. `sem_base` e um estado, nao um zero: anterior nulo (janela "desde o
 *     inicio") ou anterior zero com atual positivo nao tem percentual honesto.
 *  3. Custo por recuperacao divide centavos por contagem e arredonda para
 *     centavo inteiro: e exibicao, nunca base de fatura.
 */

import type { Broadcast, BroadcastTotals, SenderKind } from './types';

export type Direcao = 'alta' | 'queda' | 'igual' | 'sem_base';

export interface Variacao {
  readonly direcao: Direcao;
  readonly absoluta: number;
  readonly relativaBps: number | null;
}

const SEM_BASE: Variacao = { direcao: 'sem_base', absoluta: 0, relativaBps: null };

export function variar(atual: number, anterior: number | null): Variacao {
  if (anterior === null || !Number.isFinite(anterior) || !Number.isFinite(atual)) {
    return SEM_BASE;
  }
  const absoluta = atual - anterior;
  if (absoluta === 0) {
    return { direcao: 'igual', absoluta: 0, relativaBps: 0 };
  }
  if (anterior === 0) {
    return { direcao: absoluta > 0 ? 'alta' : 'queda', absoluta, relativaBps: null };
  }
  return { direcao: absoluta > 0 ? 'alta' : 'queda', absoluta, relativaBps: Math.round((absoluta * 10_000) / Math.abs(anterior)) };
}

/** Centavos por unidade, inteiro; `null` sem unidade para dividir. */
export function porUnidade(cents: number, unidades: number): number | null {
  if (unidades <= 0 || !Number.isFinite(cents)) {
    return null;
  }
  return Math.round(cents / unidades);
}

/** `parte / todo` em bps inteiros; `null` quando `todo` e zero. */
export function razaoBps(parte: number, todo: number): number | null {
  if (todo <= 0) {
    return null;
  }
  return Math.round((parte * 10_000) / todo);
}

/** Projecao linear inteira de `realizado` em `decorridos` dias para `total` dias. */
export function projetar(realizado: number, decorridos: number, total: number): number | null {
  if (decorridos <= 0 || total <= 0) {
    return null;
  }
  return Math.round((realizado * total) / decorridos);
}

/** Custo de um envio: so o que ja saiu e cobrado. */
export function custoDoEnvio(envio: Broadcast): number {
  return envio.sentCount * envio.costPerMessageCents;
}

/** Totais de uma lista de envios, no mesmo formato de `BroadcastTotals`. */
export interface Totais {
  readonly broadcasts: number;
  readonly sent: number;
  readonly delivered: number;
  readonly replied: number;
  readonly paid: number;
  readonly paidViaSeller: number;
  readonly failed: number;
  readonly optOut: number;
  readonly recoveredAmountCents: number;
  readonly commissionCents: number;
  readonly costCents: number;
}

export const TOTAIS_VAZIOS: Totais = {
  broadcasts: 0,
  sent: 0,
  delivered: 0,
  replied: 0,
  paid: 0,
  paidViaSeller: 0,
  failed: 0,
  optOut: 0,
  recoveredAmountCents: 0,
  commissionCents: 0,
  costCents: 0,
};

export function totalizarEnvios(envios: readonly Broadcast[]): Totais {
  return envios.reduce<Totais>(
    (acc, e) => ({
      broadcasts: acc.broadcasts + 1,
      sent: acc.sent + e.sentCount,
      delivered: acc.delivered + e.deliveredCount,
      replied: acc.replied + e.repliedCount,
      paid: acc.paid + e.paidCount,
      paidViaSeller: acc.paidViaSeller + e.paidViaSellerCount,
      failed: acc.failed + e.failedCount,
      optOut: acc.optOut + e.optOutCount,
      recoveredAmountCents: acc.recoveredAmountCents + e.recoveredAmountCents,
      commissionCents: acc.commissionCents + e.commissionCents,
      costCents: acc.costCents + custoDoEnvio(e),
    }),
    TOTAIS_VAZIOS,
  );
}

/**
 * Soma os totais do periodo anterior que caem no recorte (empresa e canal).
 * `null` quando nao ha periodo anterior -- a tela escreve "sem base".
 */
export function totaisAnteriores(
  previous: readonly BroadcastTotals[],
  temAnterior: boolean,
  filtro: { readonly companyId: string | null; readonly senderKind: SenderKind | null },
): Totais | null {
  if (!temAnterior) {
    return null;
  }
  return previous
    .filter((p) => (filtro.companyId === null || p.companyId === filtro.companyId) && (filtro.senderKind === null || p.senderKind === filtro.senderKind))
    .reduce<Totais>(
      (acc, p) => ({
        broadcasts: acc.broadcasts + p.broadcasts,
        sent: acc.sent + p.sent,
        delivered: acc.delivered + p.delivered,
        replied: acc.replied + p.replied,
        paid: acc.paid + p.paid,
        paidViaSeller: acc.paidViaSeller,
        failed: acc.failed + p.failed,
        optOut: acc.optOut + p.optOut,
        recoveredAmountCents: acc.recoveredAmountCents + p.recoveredAmountCents,
        commissionCents: acc.commissionCents + p.commissionCents,
        costCents: acc.costCents + p.costCents,
      }),
      TOTAIS_VAZIOS,
    );
}

/** Desempenho de um modelo somado em todos os envios que o usaram. */
export interface DesempenhoDoModelo {
  readonly envios: number;
  readonly entregues: number;
  readonly pagaram: number;
  readonly recuperadoCents: number;
}

export function desempenhoDosModelos(envios: readonly Broadcast[]): ReadonlyMap<string, DesempenhoDoModelo> {
  const mapa = new Map<string, DesempenhoDoModelo>();
  for (const e of envios) {
    if (e.status === 'agendada') {
      continue;
    }
    const atual = mapa.get(e.templateId) ?? { envios: 0, entregues: 0, pagaram: 0, recuperadoCents: 0 };
    mapa.set(e.templateId, {
      envios: atual.envios + 1,
      entregues: atual.entregues + e.deliveredCount,
      pagaram: atual.pagaram + e.paidCount,
      recuperadoCents: atual.recuperadoCents + e.recoveredAmountCents,
    });
  }
  return mapa;
}

/** Dias inteiros entre dois ISO (fim exclusivo), no minimo 1. */
export function diasEntre(inicioIso: string, fimIso: string): number {
  const inicio = new Date(inicioIso).getTime();
  const fim = new Date(fimIso).getTime();
  if (Number.isNaN(inicio) || Number.isNaN(fim) || fim <= inicio) {
    return 1;
  }
  return Math.max(1, Math.round((fim - inicio) / 86_400_000));
}
